import {getPlaceholderSnapStrategy} from 'components/customer/RoomPlanner/helpers/snapping/placeholderSnapping';
import {getFacingSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/facing';
import {getLShapePanelSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/lShapePanel';
import {getUpperSideBySideSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/upperSideBySide';
import {getCornerLEndSnap} from 'components/customer/RoomPlanner/helpers/snapping/strategies/upperLCornerEnd';
import {getSnappingData} from 'components/customer/RoomPlanner/helpers/snapping/strategies/getSnappingData';
import {
    getKickPanelJointSnap,
    getLShapeKickPanelSnap,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/kickPanel';
import * as THREE from 'three';
import {facesFrontOrBack} from 'components/customer/RoomPlanner/helpers/calculations';
import {describePair} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {
    PairFacts,
    ProductFacts,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

/**
 * One row of the routing table: the pairs it takes, and the strategy that
 * snaps them, or none when the pair does not snap at all.
 */
export interface SnapRoute {
    name: string;
    when: (pair: PairFacts) => boolean;
    strategy: SnapStrategy | null;
}

const isAnyReturn = ({isReturn, isRightReturn}: ProductFacts) =>
    isReturn || isRightReturn;

// An upper cabinet unit: not a filler or panel, and not a bulkhead.
const isUpperUnit = (product: ProductFacts) =>
    product.isUpper && !product.isFiller && !product.isBulkhead;

// An upper cabinet in the plain sense: an upper unit that is neither an
// L-shaped corner nor a shaped panel.
const isPlainUpper = (product: ProductFacts) =>
    isUpperUnit(product) && !product.isCornerLShape && !product.isNonRectShape;

// L, key and U shaped panels, and L-shaped corners against anything but
// another L-shaped corner, snap by their boxes' faces: the camera-facing snap
// gives position-dependent results for them, and getSnappingData's depth
// corrections and rotated L-corner offsets suit neither.
const isShapedByBox = ({selected, other}: PairFacts) =>
    selected.isNonRectShape ||
    (selected.isCornerLShape && !other.isCornerLShape);

// A filler or panel beside a product at its own level, or with its top level
// with the other's.
const isSnappableFiller = ({selected, other, hasMatchingTopY}: PairFacts) =>
    selected.isFiller &&
    (selected.isUpper === other.isUpper || hasMatchingTopY);

// Anything beside a filler or panel: a filler only at the same level, and an
// upper filler only with its top level with the dragged product's.
const isBesideSnappableFiller = ({
    selected,
    other,
    hasMatchingTopY,
}: PairFacts) =>
    other.isFiller &&
    (!selected.isFiller || selected.isUpper === other.isUpper) &&
    (!other.isUpper || hasMatchingTopY);

// A plain base or tall cabinet meeting a return or blind corner at a right
// angle.
const isReturnAtRightAngle = ({selected, other}: PairFacts) =>
    isAnyReturn(other) &&
    !selected.isUpper &&
    !selected.isFiller &&
    !selected.isNonRectShape &&
    facesFrontOrBack(selected.direction) !== facesFrontOrBack(other.direction);

// The last row: any pair no row above takes.
const CABINETS: SnapRoute = {
    name: 'cabinets',
    when: () => true,
    strategy: getSnappingData,
};

/**
 * Which strategy snaps a pair of products, read top to bottom: the first row
 * whose condition holds decides. A new product family is a new row, placed
 * above the rows it must take pairs from.
 */
export const SNAP_ROUTES: readonly SnapRoute[] = [
    {
        name: 'otherIgnoresSnapping',
        when: ({other}) => other.ignoresSnapping,
        strategy: null,
    },
    // Appliance GLBs snap by mesh AABB. Their pivot is centred, so the
    // cabinet strategies' corner-origin math overlaps the target.
    {
        name: 'placeholder',
        when: ({selected, other}) =>
            selected.isPlaceholder || other.isPlaceholder,
        strategy: getPlaceholderSnapStrategy,
    },
    // A kick-length panel snaps at a right angle to the end of another one,
    // and into the inside corner of an L-shaped corner base.
    {
        name: 'kickPanelJoint',
        when: ({selected, other}) =>
            selected.isKickLength && other.isKickLength && !other.isUpper,
        strategy: getKickPanelJointSnap,
    },
    {
        name: 'kickPanelIntoLCorner',
        when: ({selected, other}) =>
            selected.isKickLength && other.isCornerLShape && !other.isUpper,
        strategy: getLShapeKickPanelSnap,
    },
    {
        name: 'kickPanelBesideFloorProduct',
        when: ({selected, other}) => selected.isKickLength && other.sitsOnFloor,
        strategy: null,
    },
    // A kicker frame and adjustable legs don't go together.
    {
        name: 'kickerFrameOnLegs',
        when: ({selected, other}) => selected.isFloorProduct && other.hasLegs,
        strategy: null,
    },
    // Kicker frames and kick-length panels are measured in plan, so an upper
    // overhead would otherwise be in range.
    {
        name: 'floorProductUnderUpper',
        when: ({selected, other}) =>
            (selected.isFloorProduct || selected.isKickLength) && other.isUpper,
        strategy: null,
    },
    // Above shapedByBox, which would otherwise take an L-shaped corner meeting
    // a return.
    {
        name: 'returnFacing',
        when: isReturnAtRightAngle,
        strategy: getFacingSnap,
    },
    {
        name: 'shapedByBox',
        when: isShapedByBox,
        strategy: getLShapePanelSnap,
    },
    {
        name: 'fillerFacing',
        when: (pair) =>
            isSnappableFiller(pair) || isBesideSnappableFiller(pair),
        strategy: getFacingSnap,
    },
    // An upper onto any end of an upper L-shaped corner, rotated or not
    // (CO-5446).
    {
        name: 'upperOntoLCornerEnd',
        when: ({selected, other}) =>
            other.isCornerLShape &&
            isUpperUnit(other) &&
            isUpperUnit(selected) &&
            !selected.isNonRectShape,
        strategy: getCornerLEndSnap,
    },
    // Facing the same way, side by side; at right angles, meeting as a
    // corner. Uppers facing opposite ways fall through to the cabinets row.
    {
        name: 'upperSideBySide',
        when: ({selected, other, sameDirection}) =>
            isPlainUpper(selected) &&
            isPlainUpper(other) &&
            (sameDirection ||
                facesFrontOrBack(selected.direction) !==
                    facesFrontOrBack(other.direction)),
        strategy: getUpperSideBySideSnap,
    },
    CABINETS,
];

/**
 * @param {PairFacts} pair The dragged product and one other.
 * @return {SnapRoute} The row that handles the pair. Always one: the last row
 * takes any pair.
 */
export const findSnapRoute = (pair: PairFacts): SnapRoute =>
    SNAP_ROUTES.find(({when}) => when(pair)) ?? CABINETS;

/**
 * One other product, paired with the dragged product and routed.
 */
export interface RoutedProduct {
    other: THREE.Group;
    pair: PairFacts;
    route: SnapRoute;
}

/**
 * Routes the dragged product against every other product in the room, which
 * is the routing decision handleSnapping acts on. A dragged product that
 * ignores snapping is routed against nothing. The product itself is skipped;
 * ids compare loosely, as they always have, because some are numbers and some
 * are strings.
 * @param {ProductFacts} selected The dragged product's facts for this drag.
 * @param {THREE.Group[]} products Every product in the room.
 * @return {RoutedProduct[]} Each other product with its pair and row.
 */
export const routeProducts = (
    selected: ProductFacts,
    products: THREE.Group[]
): RoutedProduct[] =>
    selected.ignoresSnapping
        ? []
        : products
              .filter(
                  (other) => other.userData.id != selected.group.userData.id
              )
              .map((other) => {
                  const pair = describePair(selected, other);

                  return {other, pair, route: findSnapRoute(pair)};
              });
